import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Tabs, Tab, Alert } from '@mui/material';
import useAuthToken from '../../hooks/useAuthToken';

const EmailDesigner = forwardRef(({ data, subject, onUpdate, isNewAutomation = false }, ref) => {
  // --- DEBUG: Log incoming props --- 
  console.log('[EmailDesigner] Received props:', { data, subject, isNewAutomation });
  // --- END DEBUG ---

  const editorContainerRef = useRef(null);
  const unlayerEditorRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubject, setEmailSubject] = useState(subject || '');
  const [error, setError] = useState(null);
  const contentLoadedRef = useRef(false);
  const [currentTab, setCurrentTab] = useState(isNewAutomation ? 0 : 1);
  const [htmlPreview, setHtmlPreview] = useState('');
  const initializedForRef = useRef(null);

  // Get the auth token at the component level
  const token = useAuthToken();

  // Simplified Initialization
  useEffect(() => {
    console.log('[EmailDesigner] Initialization useEffect triggered:', {
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
      subject,
      isNewAutomation,
      dataId: data?.id
    });

    if (!data) {
      console.log('[EmailDesigner] No data provided, skipping initialization');
      return;
    }

    if (subject) {
      console.log('[EmailDesigner] Setting email subject:', subject);
      setEmailSubject(subject);
    }

    // Determine primary content source
    const primaryHtmlContent = data.htmlContent || data.html_content || '';
    const primaryDesignJson = data.designJson || data.design_json || null;

    console.log('[EmailDesigner] Available content:', {
      htmlContentLength: primaryHtmlContent?.length || 0,
      hasDesignJson: !!primaryDesignJson,
      designJsonLength: primaryDesignJson?.length || 0
    });

    // Try to process HTML content before setting preview
    if (primaryHtmlContent) {
      console.log('[EmailDesigner] Processing HTML content for preview');
      // Set preview content through the prepare function to properly process it
      const processedHtml = prepareEmailForDisplay(primaryHtmlContent);
      setHtmlPreview(processedHtml);
      console.log('[EmailDesigner] HTML preview set, length:', processedHtml?.length || 0);
      console.log('[EmailDesigner] Preview HTML sample:', processedHtml?.substring(0, 200) + '...');
    } else {
      console.log('[EmailDesigner] No HTML content available for preview');
      // Set a default preview message
      setHtmlPreview('<p>No email content available for preview.</p>');
    }

    // Set initial tab based on isNewAutomation
    const initialTab = isNewAutomation ? 0 : 1;
    console.log('[EmailDesigner] Setting initial tab:', initialTab);
    setCurrentTab(initialTab);

  }, [data, subject, isNewAutomation]);

  // Simplified prepareEmailForDisplay
  const prepareEmailForDisplay = (htmlContent) => {
    if (!htmlContent) {
      return `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Email Preview</title>
            <style>
              body { 
                margin: 0; 
                padding: 40px; 
                font-family: Arial, sans-serif;
                background-color: #f5f5f5;
                text-align: center;
                color: #666;
              }
            </style>
          </head>
          <body>
            <p>No email content available for preview.</p>
          </body>
        </html>`;
    }

    console.log('[EmailDesigner] Processing content for preview, length:', htmlContent.length);

    // Simple approach - just wrap everything in a clean preview container
    return `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Email Preview</title>
          <style>
            html, body { 
              margin: 0;
              padding: 0;
              background-color: #ffffff;  
              font-family: Arial, sans-serif;
            }
            .preview-container {
              width: 100%;
              min-height: 100vh;
              background-color: #ffffff;
              padding: 0;
              margin: 0;
            }
          </style>
        </head>
        <body>
          <div class="preview-container">
            ${htmlContent}
          </div>
        </body>
      </html>`;
  };

  // Simplified editor initialization
  useEffect(() => {
    // --- DEBUG: Log data prop at time of useEffect execution ---
    console.log('[EmailDesigner Init useEffect] Data prop:', data);
    // --- END DEBUG ---

    if (currentTab !== 0) return;
    
    // Only initialize once per template ID to prevent resets during saves
    const templateId = data?.id || 'new';
    if (initializedForRef.current === templateId && isEditorReady) {
      console.log('[EmailDesigner] Skipping reinitialization - already initialized for template:', templateId);
      return;
    }
    
    console.log('[EmailDesigner] Initializing editor for template:', templateId);
    initializedForRef.current = templateId;

    const initializeEditor = () => {
      if (!window.unlayer) {
        setError("Failed to load email editor script.");
        return;
      }

      if (unlayerEditorRef.current) {
        try {
          unlayerEditorRef.current.destroy();
        } catch (e) {
          // Ignore cleanup errors
        }
        unlayerEditorRef.current = null;
      }

      const editorContainer = document.getElementById('email-editor-container');
      if (!editorContainer) {
        setError("Editor container element not found.");
        // Consider adding a retry mechanism if needed
        return;
      }

      try {
        // Add error handling for editor creation
        console.log('[Editor Init] Creating Unlayer editor...');
        
        unlayerEditorRef.current = window.unlayer.createEditor({
          id: 'email-editor-container',
          displayMode: 'email',
          features: {
            preview: true
          },
          appearance: {
            theme: 'light',
            panels: {
              tools: {
                dock: 'left'
              }
            }
          },
          // Add error handling options
          onError: (error) => {
            console.error('[Unlayer Editor] Error:', error);
            setError(`Editor error: ${error.message || 'Unknown error'}`);
          }
        });
        
        console.log('[Editor Init] Unlayer editor created successfully');

        // Add event listener for when editor is ready
        unlayerEditorRef.current.addEventListener('editor:ready', () => {
          console.log('[Editor Init] Editor is ready, loading content...');
          
          // Add a small delay to ensure editor is fully initialized
          setTimeout(() => {
            try {
              // Load content after editor is fully ready
              const designJson = data?.designJson || data?.design_json;
              const htmlContent = data?.htmlContent || data?.html_content;

              console.log('[Editor Init] Available content:', {
                hasDesignJson: !!designJson,
                hasHtmlContent: !!htmlContent,
                isNewAutomation,
                designJsonLength: designJson?.length || 0,
                htmlContentLength: htmlContent?.length || 0
              });

              // Always load a default design for new automations
              if (isNewAutomation || (!designJson && !htmlContent)) {
                console.log('[Editor Init] Loading default template for new automation');
                loadFallbackDesign();
              } else if (designJson && designJson.length > 0) {
                try {
                  console.log('[Editor Init] Parsing design JSON...');
                  const design = JSON.parse(designJson);
                  
                  // Validate that the design has the required structure
                  if (!design || typeof design !== 'object') {
                    console.error('[Editor Init] Invalid design object, using fallback');
                    loadFallbackDesign();
                    return;
                  }
                  
                  // Additional validation for required design properties
                  if (!design.body || typeof design.body !== 'object') {
                    console.error('[Editor Init] Design missing body object, using fallback');
                    loadFallbackDesign();
                    return;
                  }
                  
                  // Ensure rows array exists
                  if (!Array.isArray(design.body.rows)) {
                    console.log('[Editor Init] Design body missing rows array, adding empty array');
                    design.body.rows = [];
                  }
                  
                  // Ensure schemaVersion exists
                  if (!design.schemaVersion) {
                    console.log('[Editor Init] Design missing schemaVersion, setting default');
                    design.schemaVersion = 16;
                  }
                  
                  console.log('[Editor Init] Design structure:', {
                    hasBody: !!design.body,
                    hasRows: !!design.body?.rows,
                    rowCount: design.body?.rows?.length || 0,
                    schemaVersion: design.schemaVersion
                  });
                  
                  console.log('[Editor Init] Attempting to load design from designJson');
                  unlayerEditorRef.current.loadDesign(design);
                  contentLoadedRef.current = true;
                  console.log('[Editor Init] Design loaded successfully from JSON');
                } catch (e) {
                  console.error('[Editor Init] Error parsing designJson, falling back:', e);
                  setError("Error loading design JSON. Starting with default template.");
                  loadFallbackDesign();
                }
              } else {
                console.log('[Editor Init] No valid design data found, loading default template');
                loadFallbackDesign();
              }
            } catch (initError) {
              console.error('[Editor Init] Error during content loading:', initError);
              loadFallbackDesign();
            }
          }, 500); // 500ms delay to ensure editor is fully ready
        });

        setIsEditorReady(true);

      } catch (initError) {
        setError(`Failed to initialize email editor: ${initError.message}`);
        setIsEditorReady(false); // Ensure editor is marked as not ready
      }
    };

    // Helper to load a simple fallback design
    const loadFallbackDesign = () => {
      try {
        console.log('[Editor Init] Loading fallback design');
        
        // Ensure editor exists and is ready
        if (!unlayerEditorRef.current) {
          console.error('[Editor Init] Editor reference is null, cannot load fallback design');
          setError("Editor is not initialized properly");
          return;
        }
        
        const starterDesign = {
          counters: {
            u_column: 1,
            u_row: 1,
            u_content_text: 1
          },
          body: {
            id: "body_1",
            rows: [{
              id: "row_1", 
              cells: [1],
              columns: [{
                id: "column_1",
                contents: [{
                  id: "text_1",
                  type: "text",
                  values: {
                    containerPadding: "10px",
                    anchor: "",
                    fontSize: "14px",
                    color: "#000000",
                    textAlign: "left",
                    lineHeight: "140%",
                    linkStyle: {
                      inherit: true,
                      linkColor: "#0000ee",
                      linkHoverColor: "#0000ee",
                      linkUnderline: true,
                      linkHoverUnderline: true
                    },
                    _meta: {
                      htmlID: "u_content_text_1",
                      htmlClassNames: "u_content_text"
                    },
                    selectable: true,
                    draggable: true,
                    duplicatable: true,
                    deletable: true,
                    hideable: true,
                    text: "<p style=\"font-size: 14px; line-height: 140%;\"><span style=\"color: #000000; line-height: 19.6px;\">Start designing your email here...</span></p>"
                  }
                }]
              }]
            }],
            values: {
              popupPosition: "center",
              popupWidth: "600px",
              popupHeight: "auto",
              borderRadius: "10px",
              contentAlign: "center",
              contentVerticalAlign: "center",
              contentWidth: "500px",
              fontFamily: {
                label: "Arial",
                value: "arial,helvetica,sans-serif"
              },
              textColor: "#000000",
              popupBackgroundColor: "#FFFFFF",
              popupOverlay_backgroundColor: "rgba(0, 0, 0, 0.1)",
              popupCloseButton_position: "top-right",
              popupCloseButton_backgroundColor: "#DDDDDD",
              popupCloseButton_iconColor: "#000000",
              popupCloseButton_borderRadius: "0px",
              popupCloseButton_margin: "0px",
              popupCloseButton_action: {
                name: "close_popup",
                attrs: {
                  onClick: "document.querySelector('.u-popup-container').style.display = 'none';"
                }
              },
              backgroundColor: "#e7e7e7",
              preheaderText: "",
              linkStyle: {
                body: true,
                linkColor: "#0000ee",
                linkHoverColor: "#0000ee",
                linkUnderline: true,
                linkHoverUnderline: true
              }
            }
          },
          schemaVersion: 16
        };
        
        console.log('[Editor Init] Attempting to load fallback design with structure:', {
          hasCounters: !!starterDesign.counters,
          hasBody: !!starterDesign.body,
          rowCount: starterDesign.body.rows?.length || 0,
          schemaVersion: starterDesign.schemaVersion
        });
        
        // Load design with additional error handling
        try {
          unlayerEditorRef.current.loadDesign(starterDesign);
          contentLoadedRef.current = true;
          console.log('[Editor Init] Fallback design loaded successfully');
        } catch (loadError) {
          console.error('[Editor Init] Error loading fallback design:', loadError);
          // Try an even simpler design as last resort
          const minimalDesign = {
            body: {
              rows: []
            },
            schemaVersion: 16
          };
          try {
            unlayerEditorRef.current.loadDesign(minimalDesign);
            contentLoadedRef.current = true;
            console.log('[Editor Init] Minimal fallback design loaded');
          } catch (minimalError) {
            console.error('[Editor Init] Even minimal design failed:', minimalError);
            setError("Could not initialize editor with any template");
          }
        }
      } catch (finalError) {
        console.error('[Editor Init] Could not load fallback design:', finalError);
        setError("Could not initialize editor with any content.");
      }
    };

    // Load Unlayer script if not already loaded
    const existingScript = document.querySelector('script[src="https://editor.unlayer.com/embed.js"]');
    
    if (existingScript) {
      console.log('[Editor Init] Unlayer script already exists');
      // Check if window.unlayer is available
      if (window.unlayer) {
        console.log('[Editor Init] Unlayer is ready, initializing editor');
        setTimeout(initializeEditor, 100);
      } else {
        console.log('[Editor Init] Unlayer script exists but not ready, waiting...');
        // Wait for unlayer to become available
        const checkUnlayer = setInterval(() => {
          if (window.unlayer) {
            console.log('[Editor Init] Unlayer is now ready');
            clearInterval(checkUnlayer);
            setTimeout(initializeEditor, 100);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkUnlayer);
          if (!window.unlayer) {
            console.error('[Editor Init] Unlayer failed to load within timeout');
            setError("Failed to load email editor. Please refresh the page.");
          }
        }, 10000);
      }
    } else {
      console.log('[Editor Init] Loading Unlayer script...');
      let script = document.createElement('script');
      script.src = 'https://editor.unlayer.com/embed.js';
      script.async = true;
      
      script.onload = () => {
        console.log('[Editor Init] Unlayer script loaded successfully');
        setTimeout(initializeEditor, 100);
      };
      
      script.onerror = (error) => {
        console.error('[Editor Init] Failed to load Unlayer script:', error);
        setError("Failed to load email editor script. Please check your internet connection.");
      };
      
      document.body.appendChild(script);
    }

    // Clean up
    return () => {
       if (unlayerEditorRef.current) {
        try {
          unlayerEditorRef.current.destroy();
        } catch (e) {
          // Ignore cleanup error
        }
        unlayerEditorRef.current = null;
      }
      setIsEditorReady(false); // Reset ready state on cleanup/tab change
      contentLoadedRef.current = false;
      initializedForRef.current = null; // Reset initialization tracking on cleanup
      // Remove the script tag if we added it? Maybe not necessary if it handles multiple loads.
    };
  }, [currentTab, data?.id, isNewAutomation]); // Only rerun when tab, template ID, or new automation status changes

  // Simplified Save content
  const handleSave = () => {
    console.log('[EmailDesigner] Save initiated:', {
      currentTab,
      editorReady: isEditorReady,
      hasEditor: !!unlayerEditorRef.current,
      emailSubject
    });

    if (currentTab !== 0 || !unlayerEditorRef.current || !isEditorReady) {
      const errorMsg = `Editor is not active or not ready. Tab: ${currentTab}, Editor: ${!!unlayerEditorRef.current}, Ready: ${isEditorReady}`;
      console.error('[EmailDesigner]', errorMsg);
      setError(errorMsg);
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      console.log('[EmailDesigner] Calling saveDesign...');
      unlayerEditorRef.current.saveDesign((design) => {
        console.log('[EmailDesigner] Design saved successfully:', {
          hasDesign: !!design,
          designKeys: design ? Object.keys(design) : []
        });
        
        console.log('[EmailDesigner] Calling exportHtml...');
        unlayerEditorRef.current.exportHtml((exportData) => {
          const { html, design: exportedDesign } = exportData;
          console.log('[EmailDesigner] HTML exported successfully:', {
            htmlLength: html?.length || 0,
            hasExportedDesign: !!exportedDesign
          });
          
          // Create text content from HTML
          const textContent = html
            ? html.replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            : '';

          const templateData = {
            id: data?.id || null,
            designJson: JSON.stringify(design || exportedDesign),
            htmlContent: html || '',
            textContent,
            subject: emailSubject
          };

          console.log('[EmailDesigner] Template data prepared:', {
            id: templateData.id,
            subject: templateData.subject,
            htmlContentLength: templateData.htmlContent?.length || 0,
            textContentLength: templateData.textContent?.length || 0,
            designJsonLength: templateData.designJson?.length || 0
          });

          if (onUpdate) {
            console.log('[EmailDesigner] Calling onUpdate callback...');
            onUpdate(templateData);
            console.log('[EmailDesigner] onUpdate callback completed');
          } else {
            console.warn('[EmailDesigner] No onUpdate callback provided');
          }

          setIsLoading(false);
          console.log('[EmailDesigner] Save process completed successfully');
        });
      });
    } catch (error) {
      console.error('[EmailDesigner] Error saving template:', error);
      setError(`Error saving template: ${error.message}`);
      setIsLoading(false);
    }
  };

  // Simplified getTemplateData
  const getTemplateData = (callback) => {
     if (currentTab === 0) { // Edit mode
        if (!unlayerEditorRef.current || !isEditorReady) {
            setError("Editor is not ready. Cannot get template data.");
            // Provide a fallback or indicate failure?
             callback({ // Basic fallback
               id: data?.id || `temp_${Date.now()}`,
               htmlContent: "<p>Editor not ready.</p>",
               textContent: "Editor not ready.",
               subject: emailSubject
             });
            return;
        }
        try {
            unlayerEditorRef.current.saveDesign((design) => {
                unlayerEditorRef.current.exportHtml((exportData) => {
                    const { html } = exportData;
                    const textContent = html?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || '';

                    const templateData = {
                        id: data?.id || null, // Use existing ID or null for new
                        designJson: JSON.stringify(design),
                        htmlContent: html,
                        textContent: textContent,
                        subject: emailSubject
                    };
                    callback(templateData);
                });
            });
        } catch (error) {
            setError(`Error exporting template data: ${error.message}`);
            // Provide a fallback or indicate failure?
             callback({ // Basic fallback
               id: data?.id || `temp_${Date.now()}`,
               htmlContent: "<p>Error exporting data.</p>",
               textContent: "Error exporting data.",
               subject: emailSubject
             });
        }
    } else { // Preview mode
        // Return existing data (potentially updated subject)
        const templateData = {
            ...(data || {}), // Start with existing data if available
            id: data?.id || null, // Keep ID
            subject: emailSubject, // Use current subject
            // Ensure essential fields exist, falling back to preview or defaults
            htmlContent: data?.htmlContent || data?.html_content || htmlPreview || '',
            designJson: data?.designJson || data?.design_json || null,
            textContent: data?.textContent || data?.text_content || '',
        };
         // Basic text content generation if missing
         if (!templateData.textContent && templateData.htmlContent) {
           templateData.textContent = templateData.htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
         }

        callback(templateData);
    }
};

  useImperativeHandle(ref, () => ({
    getTemplateData
  }));

  // Simplified Tab Change Handler
  const handleTabChange = (event, newValue) => {
    console.log('[EmailDesigner] Tab change:', { from: currentTab, to: newValue });
    setError(null); // Clear errors on tab change
    
    if (currentTab === 0 && newValue === 1) {
      // Switching from Edit to Preview
      console.log('[EmailDesigner] Switching from Edit to Preview - exporting current HTML');
      // Export current HTML from editor to update preview
      if (unlayerEditorRef.current && isEditorReady) {
        try {
          unlayerEditorRef.current.exportHtml((exportData) => {
             console.log('[EmailDesigner] Exported HTML for preview, length:', exportData.html?.length || 0);
             const processedHtml = prepareEmailForDisplay(exportData.html || '');
             setHtmlPreview(processedHtml);
          });
        } catch (e) {
           // Error exporting, preview might be stale
           console.error('[EmailDesigner] Error exporting HTML for preview:', e);
           setError("Could not update preview from editor.");
           // Keep existing preview or use data fallback
           const currentHtml = data?.htmlContent || data?.html_content || '';
           const processedHtml = prepareEmailForDisplay(currentHtml);
           setHtmlPreview(processedHtml);
        }
      } else {
         console.log('[EmailDesigner] Editor not ready, using existing data for preview');
         // Editor wasn't ready, attempt to use existing data for preview
         const currentHtml = data?.htmlContent || data?.html_content || '';
         const processedHtml = prepareEmailForDisplay(currentHtml);
         setHtmlPreview(processedHtml);
      }
    } else if (newValue === 1) {
      // Coming directly to preview tab - make sure we have proper content
      console.log('[EmailDesigner] Going directly to Preview tab - using existing data');
      const currentHtml = data?.htmlContent || data?.html_content || '';
      const processedHtml = prepareEmailForDisplay(currentHtml);
      setHtmlPreview(processedHtml);
    }
    setCurrentTab(newValue);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Design Your Email
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          centered
        >
          <Tab label={isNewAutomation ? "Create New" : "Edit Template"} />
          {!isNewAutomation && <Tab label="Preview Existing" />}
        </Tabs>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
         <Button
             variant="contained"
             color="primary"
             onClick={handleSave}
             disabled={isLoading || (currentTab === 0 && !isEditorReady)} // Disable if loading or editor not ready in edit tab
             sx={{ ml: 1 }}
           >
             {isLoading ? "Saving..." : "Save Template"}
           </Button>
       </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Email Subject"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Typography variant="subtitle1" gutterBottom>
          Email Content
        </Typography>

        <Box sx={{ position: 'relative' }}>
          {/* Edit mode - show editor */}
          <Box
            sx={{
              display: currentTab === 0 ? 'block' : 'none',
              mb: 3,
            }}
          >
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                height: '600px',
                position: 'relative',
              }}
            >
              <div
                id="email-editor-container"
                ref={editorContainerRef} // Assign ref here if needed for direct DOM access
                style={{ width: '100%', height: '100%' }}
              ></div>

              {/* Simplified Loading Indicator */}
              { (isLoading || (currentTab === 0 && !isEditorReady)) && (
                 <Box
                   sx={{
                     position: 'absolute',
                     top: 0, left: 0, right: 0, bottom: 0,
                     display: 'flex', alignItems: 'center', justifyContent: 'center',
                     backgroundColor: 'rgba(255, 255, 255, 0.7)',
                     zIndex: 1000 // Ensure it's above the editor iframe
                   }}
                 >
                   <CircularProgress />
                   <Typography variant="body2" sx={{ ml: 2 }}>
                     {isLoading ? "Saving..." : "Loading editor..."}
                   </Typography>
                 </Box>
               )}
            </Box>
          </Box>

          {/* Preview mode - show static HTML preview */}
          <Box
            sx={{
              display: currentTab === 1 ? 'block' : 'none',
              mb: 3
            }}
          >
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                height: '600px',
                overflow: 'hidden',
                backgroundColor: '#ffffff'
              }}
            >
              {htmlPreview ? (
                <iframe
                  title="Email Preview"
                  srcDoc={prepareEmailForDisplay(htmlPreview)}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#ffffff'
                  }}
                />
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                   {/* Keep simple loading text or indicator if preview is empty */}
                   <Typography>No preview available.</Typography>
                 </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="subtitle2">
          Using This Interface:
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          • Use the tabs to switch between editing and previewing the email.
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          • Click "Save Template" to save your changes.
        </Typography>
      </Alert>
    </Box>
  );
});

export default EmailDesigner;